import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { aboutService, promotionsService, calendarService, worksService } from '../services/artistContentService';
import { getSignedImageUrl, getBucketForCategory } from '../utils/imageUpload';

export const useArtistData = () => {
  const { user, userProfile } = useAuth();
  const [artistData, setArtistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPromotionData = async (userId) => {
    try {
      // Fix destructuring by adding default value and null check
      const promotionResponse = await promotionsService?.getPromotionContent?.(userId);
      const promotionData = promotionResponse?.data || null;
      
      if (!promotionData) {
        return {
          splashImage: null,
          splashImagePath: null,
          homeImage: null,
          homeImagePath: null,
          logo: null,
          logoPath: null,
          featuredItem: ""
        };
      }

      // Get signed URLs for existing images
      const bucket = getBucketForCategory('promotion');
      const imageFields = [
        { pathKey: 'splash_image_path', urlKey: 'splashImage', localPathKey: 'splashImagePath' },
        { pathKey: 'home_image_path', urlKey: 'homeImage', localPathKey: 'homeImagePath' },
        { pathKey: 'logo_image_path', urlKey: 'logo', localPathKey: 'logoPath' }
      ];

      const promotionState = {
        featuredItem: promotionData?.featured_item || ""
      };

      for (const field of imageFields) {
        if (promotionData?.[field?.pathKey]) {
          try {
            const urlResult = await getSignedImageUrl(bucket, promotionData?.[field?.pathKey], 7200);
            promotionState[field?.urlKey] = urlResult?.data?.signedUrl || null;
            promotionState[field?.localPathKey] = promotionData?.[field?.pathKey];
          } catch (urlError) {
            console.error(`Error getting signed URL for ${field?.pathKey}:`, urlError);
            promotionState[field?.urlKey] = null;
            promotionState[field?.localPathKey] = promotionData?.[field?.pathKey];
          }
        } else {
          promotionState[field?.urlKey] = null;
          promotionState[field?.localPathKey] = null;
        }
      }

      return promotionState;
    } catch (err) {
      console.error('Error loading promotion data:', err);
      return {
        splashImage: null,
        splashImagePath: null,
        homeImage: null,
        homeImagePath: null,
        logo: null,
        logoPath: null,
        featuredItem: ""
      };
    }
  };

  const loadAboutData = async (userId) => {
    try {
      // Fix destructuring by adding default value and null check
      const aboutResponse = await aboutService?.getAboutContent?.(userId);
      const aboutData = aboutResponse?.data || null;
      
      const aboutState = {
        aboutText: aboutData?.about_text || userProfile?.bio || `${userProfile?.full_name || 'Artist'} is a talented ${userProfile?.artist_type || 'visual'} artist. Please update your biography in the profile section.`,
        bookingContact: aboutData?.booking_contact || {
          name: userProfile?.full_name || '',
          email: userProfile?.email || user?.email || '',
          phone: (userProfile?.social_links || {})?.phone || ''
        }
      };

      // Load header image if it exists
      if (aboutData?.header_image_path) {
        try {
          const bucket = getBucketForCategory('profile');
          const urlResult = await getSignedImageUrl(bucket, aboutData?.header_image_path, 7200);
          aboutState.headerImage = urlResult?.data?.signedUrl || null;
          aboutState.headerImagePath = aboutData?.header_image_path;
        } catch (urlError) {
          console.error('Error getting signed URL for header image:', urlError);
          aboutState.headerImage = null;
          aboutState.headerImagePath = aboutData?.header_image_path;
        }
      } else {
        aboutState.headerImage = null;
        aboutState.headerImagePath = null;
      }

      // Load about image if it exists (added in migration 20260326062000)
      if (aboutData?.about_image_path) {
        try {
          const bucket = getBucketForCategory('profile');
          const urlResult = await getSignedImageUrl(bucket, aboutData?.about_image_path, 7200);
          aboutState.aboutImage = urlResult?.data?.signedUrl || null;
          aboutState.aboutImagePath = aboutData?.about_image_path;
        } catch (urlError) {
          console.error('Error getting signed URL for about image:', urlError);
          aboutState.aboutImage = null;
          aboutState.aboutImagePath = aboutData?.about_image_path;
        }
      } else {
        aboutState.aboutImage = null;
        aboutState.aboutImagePath = null;
      }

      return aboutState;
    } catch (err) {
      console.error('Error loading about data:', err);
      return {
        headerImage: null,
        headerImagePath: null,
        aboutImage: null,
        aboutImagePath: null,
        aboutText: userProfile?.bio || `${userProfile?.full_name || 'Artist'} is a talented ${userProfile?.artist_type || 'visual'} artist. Please update your biography in the profile section.`,
        bookingContact: {
          name: userProfile?.full_name || '',
          email: userProfile?.email || user?.email || '',
          phone: (userProfile?.social_links || {})?.phone || ''
        }
      };
    }
  };

  const loadCalendarData = async (userId) => {
    try {
      // Fix destructuring by adding default value and null check
      const calendarResponse = await calendarService?.getEvents?.(userId);
      const calendarData = calendarResponse?.data || null;
      return Array.isArray(calendarData) ? calendarData : [];
    } catch (err) {
      console.error('Error loading calendar data:', err);
      return [];
    }
  };

  const loadWorksData = async (userId) => {
    try {
      // Fix destructuring by adding default value and null check
      const worksResponse = await worksService?.getWorks?.(userId);
      const worksData = worksResponse?.data || null;
      
      if (!worksData || !Array.isArray(worksData) || worksData?.length === 0) {
        return [];
      }

      // Convert database works to component format with signed URLs
      const convertedWorks = await Promise.all(
        worksData?.map(async (work) => {
          if (!work) return null;

          let convertedWork = {
            id: work?.id,
            title: work?.title,
            image: null,
            imagePath: work?.image_path,
            sortOrder: work?.sort_order || 0
          };

          // Load signed URL for image if path exists
          if (work?.image_path) {
            try {
              const bucket = getBucketForCategory('work');
              const urlResult = await getSignedImageUrl(bucket, work?.image_path, 7200);
              if (urlResult?.data?.signedUrl) {
                convertedWork.image = urlResult?.data?.signedUrl;
              }
            } catch (urlError) {
              console.error('Error getting signed URL for work image:', urlError);
            }
          }

          // Add type-specific fields based on work_type
          const workType = work?.work_type || 'visual';
          
          if (workType === 'visual') {
            convertedWork = {
              ...convertedWork,
              mediaType: work?.media_type || "",
              dimensions: work?.dimensions || "",
              price: work?.price || 0,
              collectible: work?.collectible || false,
              printSales: work?.print_sales || false,
              includeInShop: work?.include_in_shop || false,
              availabilityDate: work?.availability_date || "",
              removalDate: work?.removal_date || "",
              creationDate: work?.creation_date || ""
            };
          } else if (workType === 'model') {
            convertedWork = {
              ...convertedWork,
              workDate: work?.work_date || "",
              designer: work?.designer || "",
              photographer: work?.photographer || "",
              url: work?.external_url || "",
              videoLink: work?.video_link || ""
            };
          } else if (workType === 'actor') {
            convertedWork = {
              ...convertedWork,
              workDate: work?.work_date || "",
              workType: work?.work_type || "",
              director: work?.director || "",
              photographer: work?.photographer || "",
              url: work?.external_url || "",
              characterName: work?.character_name || "",
              videoLink: work?.video_link || ""
            };
          } else if (workType === 'music') {
            // For music artists, return the music platforms directly
            return work?.music_platforms || {
              spotify: "",
              apple: "",
              tidal: "",
              youtube: ""
            };
          }

          return convertedWork;
        })
      );

      // Filter out null values from the array
      return convertedWorks?.filter(work => work !== null) || [];
    } catch (err) {
      console.error('Error loading works data:', err);
      return [];
    }
  };

  useEffect(() => {
    const initializeArtistData = async () => {
      if (!userProfile || !user) {
        setLoading(false);
        return;
      }

      try {
        const socialLinks = userProfile?.social_links || {};
        
        // Load artist logo from user_profiles.artist_logo_url
        let artistLogoUrl = null;
        if (userProfile?.artist_logo_url) {
          try {
            const bucket = getBucketForCategory('profile'); // Use artist-profile-images bucket
            const logoResult = await getSignedImageUrl(bucket, userProfile?.artist_logo_url, 7200);
            artistLogoUrl = logoResult?.data?.signedUrl || null;
          } catch (logoError) {
            console.error('Error loading artist logo:', logoError);
            artistLogoUrl = null;
          }
        }
        
        // Load data from database with improved error handling
        const [promotionData, aboutData, calendarData, worksData] = await Promise.allSettled([
          loadPromotionData(user?.id),
          loadAboutData(user?.id),
          loadCalendarData(user?.id),
          loadWorksData(user?.id)
        ]);

        // Extract successful results or use defaults
        const finalPromotionData = promotionData?.status === 'fulfilled' ? promotionData?.value : {
          splashImage: null,
          splashImagePath: null,
          homeImage: null,
          homeImagePath: null,
          logo: null,
          logoPath: null,
          featuredItem: ""
        };

        const finalAboutData = aboutData?.status === 'fulfilled' ? aboutData?.value : {
          headerImage: null,
          headerImagePath: null,
          aboutImage: null,
          aboutImagePath: null,
          aboutText: userProfile?.bio || `${userProfile?.full_name || 'Artist'} is a talented ${userProfile?.artist_type || 'visual'} artist. Please update your biography in the profile section.`,
          bookingContact: {
            name: userProfile?.full_name || '',
            email: userProfile?.email || user?.email || '',
            phone: (userProfile?.social_links || {})?.phone || ''
          }
        };

        const finalCalendarData = calendarData?.status === 'fulfilled' ? calendarData?.value : [];
        const finalWorksData = worksData?.status === 'fulfilled' ? worksData?.value : [];

        const initialData = {
          id: userProfile?.id || user?.id,
          name: userProfile?.full_name || user?.email?.split('@')?.[0] || '',
          email: userProfile?.email || user?.email || '',
          artistType: userProfile?.artist_type || 'visual',
          profile: {
            name: userProfile?.full_name || user?.email?.split('@')?.[0] || '',
            email: userProfile?.email || user?.email || '',
            phone: socialLinks?.phone || '',
            website: userProfile?.website || '',
            // CRITICAL FIX: Prioritize database highlight_color column
            highlightColor: userProfile?.highlight_color || (userProfile?.settings || {})?.highlightColor || '#6366F1',
            secondaryColor: userProfile?.secondary_color || (userProfile?.settings || {})?.secondaryColor || '#6366F1',
            backgroundColor: userProfile?.background_color || (userProfile?.settings || {})?.backgroundColor || '#000000',
            artist_type: userProfile?.artist_type || 'musician',
            // Add artist logo URL and path to profile data
            artist_logo_url: userProfile?.artist_logo_url || '',
            artistLogoUrl: artistLogoUrl, // Signed URL for display
            // Load settings properly with database priority
            settings: {
              highlightColor: userProfile?.highlight_color || (userProfile?.settings || {})?.highlightColor || '#6366F1',
              ...(userProfile?.settings || {})
            },
            instagram: socialLinks?.instagram || '',
            twitter: socialLinks?.twitter || '',
            facebook: socialLinks?.facebook || '',
            imdb: socialLinks?.imdb || '',
            linkedin: socialLinks?.linkedin || '',
            // Handle both old and new artist music URL key formats
            artistSpotify: socialLinks?.artistSpotify || socialLinks?.spotify || '',
            artistAppleMusic: socialLinks?.artistAppleMusic || socialLinks?.apple_music || '',
            artistYouTubeMusic: socialLinks?.artistYouTubeMusic || socialLinks?.youtube_music || '',
            artistTidal: socialLinks?.artistTidal || socialLinks?.tidal || '',
            // Also include the old format keys for compatibility  
            spotify: socialLinks?.spotify || socialLinks?.artistSpotify || '',
            apple_music: socialLinks?.apple_music || socialLinks?.artistAppleMusic || '',
            youtube_music: socialLinks?.youtube_music || socialLinks?.artistYouTubeMusic || '',
            tidal: socialLinks?.tidal || socialLinks?.artistTidal || '',
            social_links: socialLinks
          },
          promotion: finalPromotionData,
          about: finalAboutData,
          calendar: finalCalendarData,
          work: finalWorksData,
          shop: {
            tshirts: false,
            posters: false,
            prints: false,
            bags: false,
            blankets: false,
            sweatshirts: false,
            vinylAlbums: false
          }
        };

        setArtistData(initialData);
        setError(null);
      } catch (err) {
        console.error('Error initializing artist data:', err);
        setError('Failed to load artist profile');
      } finally {
        setLoading(false);
      }
    };

    initializeArtistData();
  }, [user, userProfile]);

  const updateArtistData = async (section, data) => {
    if (!user) return { error: { message: 'No user logged in' } };

    try {
      setArtistData(prev => ({
        ...prev,
        [section]: data
      }));

      // Handle database operations for different sections
      if (section === 'profile' || section === 'about') {
        const updates = {};
        
        if (section === 'profile') {
          updates.full_name = data?.name;
          updates.email = data?.email;
          updates.website = data?.website;
          updates.artist_type = data?.artist_type;
          
          // CRITICAL FIX: Save highlight color to database
          updates.highlight_color = data?.highlightColor; // Direct column update

          // Save background color and secondary color to database
          updates.background_color = data?.backgroundColor; // Direct column update
          updates.secondary_color = data?.secondaryColor; // Direct column update
          
          // CRITICAL: Save artist logo URL to database
          updates.artist_logo_url = data?.artist_logo_url || ''; // Direct column update
          
          // Also store in settings for backward compatibility
          const currentSettings = userProfile?.settings || {};
          updates.settings = {
            ...currentSettings,
            highlightColor: data?.highlightColor,
            backgroundColor: data?.backgroundColor,
            secondaryColor: data?.secondaryColor
          };
          
          // Preserve all existing social_links and add/update new ones
          const currentSocialLinks = userProfile?.social_links || {};
          updates.social_links = {
            ...currentSocialLinks, // Keep all existing social links
            phone: data?.phone || '',
            instagram: data?.instagram || '',
            twitter: data?.twitter || '',
            facebook: data?.facebook || '',
            imdb: data?.imdb || '',
            linkedin: data?.linkedin || '',
            // Store artist music URLs with consistent keys
            artistSpotify: data?.artistSpotify || '',
            artistAppleMusic: data?.artistAppleMusic || '',
            artistYouTubeMusic: data?.artistYouTubeMusic || '',
            artistTidal: data?.artistTidal || '',
            // Also maintain backward compatibility with old keys
            spotify: data?.artistSpotify || data?.spotify || '',
            apple_music: data?.artistAppleMusic || data?.apple_music || '',
            youtube_music: data?.artistYouTubeMusic || data?.youtube_music || '',
            tidal: data?.artistTidal || data?.tidal || ''
          };

          // CRITICAL: Update auth.users email if email has changed
          if (data?.email && data?.email !== user?.email) {
            try {
              console.log('Syncing email change to auth system:', data?.email);
              
              const { error: authError } = await supabase?.auth?.updateUser({
                email: data?.email
              });
              
              if (authError) {
                console.warn('Auth email update warning:', authError?.message);
                
                // Only fail if it's a critical error, not email confirmation
                if (!authError?.message?.includes('email') && 
                    !authError?.message?.includes('confirmation') &&
                    !authError?.message?.includes('link')) {
                  return { 
                    error: { 
                      message: `Profile updated but failed to sync login email: ${authError?.message}. Please contact support.`
                    }
                  };
                }
              }
              
              console.log('✅ Email successfully synced to authentication system');
            } catch (authUpdateError) {
              console.warn('Auth email sync failed:', authUpdateError);
              return {
                error: {
                  message: `Profile updated but login email sync failed. Please log out and log back in with your new email address.`
                }
              };
            }
          }
        }
        
        if (section === 'about' && data?.aboutText) {
          updates.bio = data?.aboutText;
        }

        const { error: updateError } = await supabase
          ?.from('user_profiles')
          ?.update(updates)
          ?.eq('id', user?.id);

        if (updateError) {
          console.error('Database update error:', updateError);
          return { error: updateError };
        }

        // Save about content to artist_about table if about section
        if (section === 'about') {
          try {
            const aboutResult = await aboutService?.saveAboutContent?.(user?.id, {
              headerImagePath: data?.headerImagePath,
              aboutImagePath: data?.aboutImagePath,
              aboutText: data?.aboutText,
              bookingContact: data?.bookingContact
            });

            if (aboutResult?.error) {
              console.error('About section save error:', aboutResult?.error);
              return { error: aboutResult?.error };
            }
          } catch (aboutError) {
            console.error('About section save error:', aboutError);
            return { error: { message: 'Failed to save about content' } };
          }
        }
      }

      // Handle promotion section
      if (section === 'promotion') {
        try {
          const promotionResult = await promotionsService?.savePromotionContent?.(user?.id, {
            splashImagePath: data?.splashImagePath,
            homeImagePath: data?.homeImagePath,
            logoImagePath: data?.logoPath,
            featuredItem: data?.featuredItem
          });

          if (promotionResult?.error) {
            console.error('Promotion section save error:', promotionResult?.error);
            return { error: promotionResult?.error };
          }
        } catch (promotionError) {
          console.error('Promotion section save error:', promotionError);
          return { error: { message: 'Failed to save promotion content' } };
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating artist data:', error);
      return { error: { message: 'Failed to update profile' } };
    }
  };

  return {
    artistData,
    loading,
    error,
    updateArtistData
  };
};